import React, { useState } from 'react';
import {
  Paper, Typography, Box, TextField, Button, Grid, Avatar, IconButton,
  Tooltip, CircularProgress, Alert, Snackbar, Chip, Divider, Collapse, InputAdornment
} from '@mui/material';
import { PhotoCamera, Edit, Save, Cancel, Visibility, VisibilityOff, LockOutlined } from '@mui/icons-material';
import { uploadProfilePhoto, updateProfile, changePassword } from '../../api/user';
import { alpha } from '@mui/material/styles';

const ProfileDetails = ({ user, setUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [editData, setEditData] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    preferredLanguage: user?.preferredLanguage || 'en'
  });
  // Password change states
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const fileInputRef = React.useRef(null);

  const handlePhotoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setNotification({
        open: true,
        message: 'Please select a valid image file (JPG, PNG, or WebP)',
        severity: 'error'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setNotification({
        open: true,
        message: 'File size must be less than 5MB',
        severity: 'error'
      });
      return;
    }

    setUploading(true);
    try {
      const response = await uploadProfilePhoto(file);

      // Create a new photo URL with timestamp to force refresh
      const newPhotoPath = response.data.profilePhotoPath + '?t=' + Date.now();

      // Update user state immediately
      setUser(prev => ({
        ...prev,
        profilePhotoPath: newPhotoPath
      }));

      setNotification({
        open: true,
        message: 'Profile photo updated successfully! 📸',
        severity: 'success'
      });
    } catch (error) {
      console.error('Photo upload failed:', error);
      setNotification({
        open: true,
        message: 'Failed to upload photo. Please try again.',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(editData);
      setUser(prev => ({ ...prev, ...editData }));
      setIsEditing(false);
      setNotification({
        open: true,
        message: 'Profile updated successfully! ✨',
        severity: 'success'
      });
    } catch (error) {
      console.error('Profile update failed:', error);
      setNotification({
        open: true,
        message: 'Failed to update profile. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      displayName: user?.displayName || '',
      email: user?.email || '',
      preferredLanguage: user?.preferredLanguage || 'en'
    });
    setIsEditing(false);
  };

  // Password change handlers
  const handlePasswordChange = (field) => (e) => {
    setPasswordData({
      ...passwordData,
      [field]: e.target.value
    });
    // Clear errors when typing
    if (passwordErrors[field]) {
      setPasswordErrors({
        ...passwordErrors,
        [field]: null
      });
    }
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'New password must be at least 8 characters long';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSave = async () => {
    if (!validatePasswordForm()) return;

    setChangingPassword(true);
    try {
      const response = await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setNotification({
        open: true,
        message: 'Password changed successfully! 🔐',
        severity: 'success'
      });

      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordSection(false);
    } catch (error) {
      console.error('Password change failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed to change password. Please try again.';
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      if (errorMessage.includes('Current password is incorrect')) {
        setPasswordErrors({
          ...passwordErrors,
          currentPassword: 'Current password is incorrect'
        });
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const getUserLevel = (points) => {
    if (points >= 1000) return { name: "Eco Master", color: "#e53935", icon: "🏆" };
    if (points >= 500) return { name: "Earth Guardian", color: "#00b894", icon: "🌍" };
    if (points >= 250) return { name: "Recycle Master", color: "#ffc400", icon: "♻️" };
    if (points >= 100) return { name: "Eco Warrior", color: "#66bb6a", icon: "⚔️" };
    return { name: "Green Rookie", color: "#43a047", icon: "🌱" };
  };

  const userLevel = getUserLevel(user?.rewardsPoints || 0);

  return (
    <>
      <Paper elevation={4} sx={{
        p: 4,
        borderRadius: 4,
        background: 'linear-gradient(135deg, #ffffff, #f5fbf7)',
        border: '2px solid #43a04715'
      }}>
        <Typography variant="h4" fontWeight={800} gutterBottom sx={{
          color: '#43a047',
          background: 'linear-gradient(135deg, #43a047, #66bb6a)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          👤 Profile Details
        </Typography>

        <Grid container spacing={4} alignItems="flex-start">
          {/* Profile Photo Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                <Avatar
                  src={user?.profilePhotoPath}
                  alt={user?.displayName || "User"}
                  sx={{
                    width: 160,
                    height: 160,
                    border: '6px solid white',
                    boxShadow: '0 12px 40px rgba(67, 160, 71, 0.15)',
                    fontSize: '3rem',
                    background: 'linear-gradient(135deg, #43a047, #66bb6a)'
                  }}
                >
                  {user?.displayName?.charAt(0) || 'U'}
                </Avatar>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />

                <Tooltip title="Change Profile Photo">
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      backgroundColor: '#43a047',
                      color: 'white',
                      boxShadow: '0 4px 20px rgba(67, 160, 71, 0.3)',
                      '&:hover': {
                        backgroundColor: '#66bb6a',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {uploading ? (
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : (
                      <PhotoCamera />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* User Level Badge */}
              <Chip
                label={`${userLevel.icon} ${userLevel.name}`}
                sx={{
                  background: `linear-gradient(135deg, ${userLevel.color}, ${alpha(userLevel.color, 0.8)})`,
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 2,
                  py: 1,
                  boxShadow: `0 4px 20px ${alpha(userLevel.color, 0.3)}`
                }}
              />
            </Box>
          </Grid>

          {/* Profile Information */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight={600} color="#1b5e20">
                Personal Information
              </Typography>
              {!isEditing ? (
                <Button
                  startIcon={<Edit />}
                  onClick={() => setIsEditing(true)}
                  variant="outlined"
                  sx={{
                    borderColor: '#43a047',
                    color: '#43a047',
                    '&:hover': {
                      backgroundColor: '#43a04710',
                      borderColor: '#66bb6a'
                    }
                  }}
                >
                  Edit Profile
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    startIcon={<Save />}
                    onClick={handleSaveProfile}
                    disabled={saving}
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #66bb6a, #43a047)'
                      }
                    }}
                  >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                  </Button>
                  <Button
                    startIcon={<Cancel />}
                    onClick={handleCancelEdit}
                    variant="outlined"
                    color="error"
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={isEditing ? editData.displayName : user?.displayName || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, displayName: e.target.value }))}
                  disabled={!isEditing}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      '&.Mui-focused fieldset': {
                        borderColor: '#43a047'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={user?.email || ''}
                  disabled={true}
                  variant="outlined"
                  helperText="Email cannot be changed"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Preferred Language"
                  select
                  value={isEditing ? editData.preferredLanguage : user?.preferredLanguage || 'en'}
                  onChange={(e) => setEditData(prev => ({ ...prev, preferredLanguage: e.target.value }))}
                  disabled={!isEditing}
                  variant="outlined"
                  SelectProps={{
                    native: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      '&.Mui-focused fieldset': {
                        borderColor: '#43a047'
                      }
                    }
                  }}
                >
                  <option value="en">🇺🇸 English</option>
                  <option value="bn">🇧🇩 বাংলা (Bengali)</option>
                  <option value="hi">🇮🇳 हिन्दी (Hindi)</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Eco Points"
                  value={`⭐ ${user?.rewardsPoints || 0} points`}
                  disabled={true}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #ffc40010, #ffffff)'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Status"
                  value="🟢 Active"
                  disabled={true}
                  variant="outlined"
                  helperText="Account is active and verified"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #43a04710, #ffffff)'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Member Since"
                  value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'Unknown'}
                  disabled={true}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Password Change Section Toggle */}
            <Box sx={{ mt: 4 }}>
              <Button
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                startIcon={<LockOutlined />}
                variant="outlined"
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1976d210',
                    borderColor: '#1976d2'
                  }
                }}
              >
                {showPasswordSection ? 'Hide Password Change' : 'Change Password'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Password Change Section */}
        <Collapse in={showPasswordSection}>
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }}>
              <Chip
                label="Password Change"
                icon={<LockOutlined />}
                sx={{
                  px: 2,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1976d210, #2196f310)',
                }}
              />
            </Divider>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="body1" fontWeight={500} color="#546e7a" mb={2}>
                  Change your password using this form. For security reasons, you need to provide your current password.
                </Typography>
                <Typography variant="body2" color="#e53935" fontWeight={600}>
                  Password Requirements:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <Typography component="li" variant="body2" color="#546e7a">
                    At least 8 characters long
                  </Typography>
                  <Typography component="li" variant="body2" color="#546e7a">
                    Should be different from your current password
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange('currentPassword')}
                      error={!!passwordErrors.currentPassword}
                      helperText={passwordErrors.currentPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              edge="end"
                            >
                              {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2'
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange('newPassword')}
                      error={!!passwordErrors.newPassword}
                      helperText={passwordErrors.newPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2'
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange('confirmPassword')}
                      error={!!passwordErrors.confirmPassword}
                      helperText={passwordErrors.confirmPassword}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2'
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowPasswordSection(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                        setPasswordErrors({});
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      disabled={changingPassword}
                      onClick={handlePasswordSave}
                      sx={{
                        background: 'linear-gradient(135deg, #1976d2, #2196f3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2196f3, #1976d2)'
                        }
                      }}
                    >
                      {changingPassword ? (
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                      ) : (
                        'Save New Password'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ borderRadius: 3 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfileDetails;
